import { BuildOutlined } from '@ant-design/icons';
import { Order } from '@services';
import { Tag, Tooltip } from 'antd';
import React from 'react';
import { useNavigate } from 'react-router-dom';

interface IProps {
  /** Any object carrying the flattened build fields - an Order row, the job card, a calendar event. */
  order?: Pick<Order, 'masterBuildId' | 'masterBuildNumber' | 'masterBuildName'>;
  masterBuildId?: string;
  number?: string;
  name?: string;
  /** Drops the name and keeps the number, for narrow table columns. */
  compact?: boolean;
  size?: 'small' | 'default';
}

/**
 * Marks a job as one leg of a build, showing both the build number and the build's name.
 * Clicking it opens the build. Renders nothing for ordinary standalone bookings, so it is safe to
 * drop into any job row unconditionally.
 */
export const MasterBuildTag: React.FC<IProps> = ({
  order,
  masterBuildId,
  number,
  name,
  compact = false,
  size = 'default',
}) => {
  const navigate = useNavigate();

  const id = masterBuildId ?? order?.masterBuildId;
  const buildNumber = number ?? order?.masterBuildNumber;
  const buildName = name ?? order?.masterBuildName;

  if (!id || !buildNumber) return null;

  const label = compact || !buildName ? buildNumber : `${buildNumber} · ${buildName}`;

  return (
    <Tooltip title={buildName ? `${buildNumber} · ${buildName}` : buildNumber}>
      <Tag
        icon={<BuildOutlined />}
        color="purple"
        style={{
          cursor: 'pointer',
          maxWidth: compact ? 140 : 260,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: size === 'small' ? 11 : undefined,
        }}
        // Stops the click reaching the row, which would open the job instead of the build.
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/master-builds/${id}`);
        }}
      >
        {label}
      </Tag>
    </Tooltip>
  );
};
